import type { WidgetElement, PageConfig } from "./types.ts";
import widgetCSS from "./widget.css?inline";
import { getPageConfig } from "./utils/config.ts";
import { getDynamicStyles } from "./utils/dynamicStyles.ts";
import { parseExperiment } from "./utils/experiments.ts";
import { loadVariant } from "./variantLoader.ts";

// Inject CSS styles into the page
function injectStyles(dynamicStyles: string): void {
  // Check if styles are already injected
  if (document.querySelector("#rw-widget-styles")) {
    return;
  }

  const styleTag = document.createElement("style");
  styleTag.id = "rw-widget-styles";
  styleTag.textContent = `${dynamicStyles}${widgetCSS}`;
  document.head.appendChild(styleTag);
}

// Show loading state
function showLoadingState(el: WidgetElement): void {
  el.innerHTML = `
    <div class="rw-widget rw-widget-loading">
      <div class="rw-flex rw-items-center rw-justify-center rw-p-4">
        <div class="rw-animate-spin rw-w-4 rw-h-4 rw-border-2 rw-border-blue rw-border-t-transparent rw-rounded-full" 
             role="status" 
             aria-label="Loading recommendations">
        </div>
        <span class="rw-ml-2 rw-text-sm rw-text-subtle">Loading recommendations...</span>
        <span class="rw-sr-only">Please wait while we load personalized content recommendations</span>
      </div>
    </div>
  `;
}

async function initWidget(
  el: WidgetElement,
  pageConfig: PageConfig
): Promise<void> {
  try {
    // Show loading state
    showLoadingState(el);

    // Get the experiment variant
    const experimentAttr: string = el.dataset.experiment || "default";
    const experiment = parseExperiment(experimentAttr);
    
    // Dynamically load the variant renderer
    const renderer = await loadVariant(experimentAttr);

    // Render the widget
    const widget: HTMLElement | null = await renderer({
      pageConfig,
      experiment,
    });

    // Clear loading state and append widget
    el.innerHTML = "";

    if (!widget) {
      console.debug("[MM Recirculation Widget] Widget didn't return element", {
        experiment,
        element: el,
      });
      return;
    }

    el.appendChild(widget);

    // Log successful initialization
    console.debug("[MM Recirculation Widget] Widget initialized successfully", {
      experiment,
      element: el,
    });
  } catch (error) {
    console.error(
      "[MM Recirculation Widget] Failed to initialize widget:",
      error
    );
  }
}

// Widget initialization function
async function initializeWidgets(): Promise<void> {
  try {
    console.debug("[MM Recirculation Widget] Starting widget initialization");

    const pageConfig = await getPageConfig();

    // Inject CSS styles first
    const dynamicStyles = getDynamicStyles(pageConfig);
    injectStyles(dynamicStyles);

    // Get all widget elements
    const widgets = [
      ...document.querySelectorAll<WidgetElement>("#mm-recirc"),
      ...document.querySelectorAll<WidgetElement>("[data-mm-recirc]"),
    ];

    if (widgets.length === 0) {
      console.warn("[MM Recirculation Widget] No widget elements found");
      return;
    }

    console.debug(
      `[MM Recirculation Widget] Found ${widgets.length} widget element(s)`
    );

    // Initialize all widgets
    await Promise.allSettled(widgets.map((el) => initWidget(el, pageConfig)));

    console.debug("[MM Recirculation Widget] All widgets initialized");
  } catch (error) {
    console.error(
      "[MM Recirculation Widget] Failed to initialize widgets:",
      error
    );
  }
}

// Initialize widgets on page load or immediately if DOM is already ready
if (document.readyState === "loading") {
  // DOM is still loading, wait for DOMContentLoaded
  document.addEventListener("DOMContentLoaded", initializeWidgets);
} else {
  // DOM is already loaded (interactive or complete), initialize immediately
  initializeWidgets();
}
